import { BookOpen } from "lucide-react";
import type { GuideLinkedEvent } from "@shared/guideEvents";

interface GuidePicksRowProps {
  picks: GuideLinkedEvent[];
  onOpenEvent: (event: GuideLinkedEvent, position: number) => void;
  onOpenGuide: (type: "week" | "weekend") => void;
}

function localIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "Today", "Tomorrow", or a short weekday such as "Sat". */
export function formatPickDay(eventDate: string | null, now: Date = new Date()): string {
  if (!eventDate) return "";
  const iso = eventDate.slice(0, 10);
  const today = localIsoDate(now);
  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(now.getDate() + 1);
  if (iso === today) return "Today";
  if (iso === localIsoDate(tomorrowDate)) return "Tomorrow";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" });
}

/**
 * Upcoming events named in a guide, one per event, in guide order.
 * Past events are dropped so a finished weekend never shows stale picks.
 */
export function selectGuidePicks(
  events: GuideLinkedEvent[],
  now: Date = new Date(),
  max = 6,
): GuideLinkedEvent[] {
  const today = localIsoDate(now);
  const seen = new Set<string>();
  const picks: GuideLinkedEvent[] = [];
  for (const event of events) {
    const iso = event.eventDate?.slice(0, 10);
    if (!iso || iso < today || seen.has(event.id)) continue;
    seen.add(event.id);
    picks.push(event);
    if (picks.length === max) break;
  }
  return picks;
}

/**
 * A compact row of the weekend guide's picks, shown inside the event list after the
 * first few cards. Each pick opens the event drawer; the header links to the guides.
 */
export function GuidePicksRow({ picks, onOpenEvent, onOpenGuide }: GuidePicksRowProps) {
  return (
    <section
      aria-labelledby="guide-picks-heading"
      className="col-span-full rounded-[18px] border border-[#d9e6dc] bg-[#f5f8f3] px-3 py-3 md:px-4"
      data-testid="guide-picks-row"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h2 id="guide-picks-heading" className="flex items-center gap-1.5 text-sm font-semibold text-[#292721]">
          <BookOpen className="h-4 w-4 shrink-0 text-[#16795e]" aria-hidden="true" />
          This weekend&apos;s picks
        </h2>
        <div className="flex items-center gap-3 text-xs font-semibold">
          <button
            type="button"
            onClick={() => onOpenGuide("weekend")}
            aria-haspopup="dialog"
            className="min-h-[44px] text-[#16795e] underline underline-offset-2 hover:text-[#0f4f3c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
            data-testid="guide-picks-weekend-guide"
          >
            Read the weekend guide
          </button>
          <button
            type="button"
            onClick={() => onOpenGuide("week")}
            aria-haspopup="dialog"
            className="min-h-[44px] text-[#16795e] underline underline-offset-2 hover:text-[#0f4f3c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] md:hidden"
            data-testid="guide-picks-week-guide"
          >
            This week
          </button>
        </div>
      </div>
      <ul className="-mx-1 mt-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1" role="list">
        {picks.map((event, index) => (
          <li key={event.id} className="flex w-[min(15rem,75vw)] shrink-0 snap-start md:w-auto md:min-w-0 md:flex-1">
            <button
              type="button"
              onClick={() => onOpenEvent(event, index + 1)}
              className="flex w-full flex-col rounded-[14px] border border-[#e3ddd3] bg-white px-3 py-2.5 text-left transition-colors hover:border-[#8fbb9f] hover:bg-[#f6faf6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
              data-testid="guide-pick"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#16795e]">
                {[formatPickDay(event.eventDate), event.time].filter(Boolean).join(" · ")}
              </span>
              <span className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug text-[#292721]">
                {event.title}
              </span>
              <span className="mt-1 truncate text-xs text-[#746d62]">
                {[event.venue, event.region].filter(Boolean).join(" · ")}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
